import React, { forwardRef, useCallback, useState } from 'react';
import Router from 'next/router';

import { IAdvertising } from '@r1-frontend/api-domru/spec-offer/v1/banners/dto/responseDto';

import { push } from '@r1-frontend/analytics/campaignLayer';
import { dataLayerPush } from '@r1-frontend/analytics/dataLayerPush';

import { btnTypes } from '@r1-frontend/ui-react/components/buttons/baseButton';
import { TButtonSize, TButtonType } from '@r1-frontend/ui-react/components/buttons/button';
import WebpImage from '@r1-frontend/ui-react/components/WebpImage';
import { NonStaticContainer } from '@r1-frontend/ui-react/experimental/containers';
import { TTooltipPosition } from '@r1-frontend/ui-react/experimental/tooltip/types';
import { AdvertisingLabelWithTooltip } from '@r1-frontend/ui-react/organisms/AdvertisingLabelWithTooltip';
import { COLORS } from '@r1-frontend/ui-react/tokens/colors';
import { EVENTS } from '@r1-frontend/shared/constants/analytics';
import { useWebp } from '@r1-frontend/shared/hooks/useWebp';

import { IBannerTag as ITag } from '~/src/api/content/banners';

import * as ST from './styled';

export enum EClickType {
    ON_BANNER = ' full',
    ON_BTN = '',
}

type TCustomStyleRule = Record<string, string>;
type TCustomStyleType = 'sMobile' | 'mobile' | 'tablet' | 'laptop' | 'desktop' | 'default';
export type TCustomStyle = {
    [key in TCustomStyleType]?: TCustomStyleRule
}
export type TExtStyle = Record<string, TCustomStyle>

interface IProps {
    label?: string,
    labelColor?: string,
    title?: string,
    description?: string,
    image?: string,
    imageWebp?: string,
    background?: string,
    backgroundWebp?: string,
    buttonText?: string,
    textColor?: string,
    route?: string,
    tags: ITag[],
    bannerIdx?: string | number,
    buttonType?: TButtonType,
    legal?: {
        text?: string,
        textColor?: string,
        ageLimit?: string,
    },
    extStyles?: TExtStyle,
    onClick?: () => void,
    buttonSize?: TButtonSize,
    communicationId?: string,
    advertising?: IAdvertising,
    tooltipPosition?: TTooltipPosition,
}

const PromoBanner = forwardRef<HTMLDivElement, IProps>(({
    bannerIdx = 0,
    label,
    labelColor,
    title = '',
    description = '',
    buttonText = 'Подробнее',
    image = '',
    imageWebp = '',
    background = '',
    backgroundWebp = '',
    textColor = COLORS.TextPrimary,
    tags = [],
    route = '',
    onClick,
    buttonType = btnTypes.PRIMARY,
    legal = {
        textColor: COLORS.TextPrimary,
    },
    extStyles,
    buttonSize,
    communicationId,
    advertising,
    tooltipPosition = 'bottom-right',
}, ref): JSX.Element => {
    const [isRedirecting, setIsRedirecting] = useState(false);

    const sendDataLayerPush = useCallback((clickType: EClickType) => {
        const [firstTag, lastTag] = tags;
        const lastTagAlias = lastTag?.alias.replaceAll('_', ' ');

        dataLayerPush({
            event: EVENTS.UAevent,
            category: `banner ${firstTag?.alias}`,
            action: `click ${bannerIdx} ${lastTagAlias} banner${clickType}`,
            label: route,
        });
    }, [tags, bannerIdx, route]);

    const onClickBanner = useCallback((clickType: EClickType) => {
        if (route) {
            if (communicationId) {
                push({ id: +bannerIdx, type: 'banner', actionType: 'transition', communicationId });
            }

            sendDataLayerPush(clickType);
            setIsRedirecting(true);
            Router.push(route);
        }

        onClick && onClick();
    }, [sendDataLayerPush, route, onClick]);

    const backgroundImage = useWebp() ? backgroundWebp : background;

    return (
        <ST.Wrapper
            ref={ref}
            onClick={() => onClickBanner(EClickType.ON_BANNER)}
            backgroundImage={backgroundImage}
            extStyles={extStyles?.wrapper}
        >
            <ST.DescCol lg={6} md={6} sm={6} xs={6} extStyles={extStyles?.descCol}>
                <ST.Desc extStyles={extStyles?.description}>
                    {!!label && (
                        <ST.BannerLabel
                            textColor={labelColor}
                            dangerouslySetInnerHTML={{ __html: label }}
                        />
                    )}
                    <ST.Title
                        textColor={textColor}
                        dangerouslySetInnerHTML={{ __html: title }}
                        extStyles={extStyles?.title}/>
                    <ST.Text
                        textColor={textColor}
                        dangerouslySetInnerHTML={{ __html: description }}
                        extStyles={extStyles?.text}/>
                    <ST.SButton
                        size={buttonSize}
                        href={route}
                        onClick={() => onClickBanner(EClickType.ON_BTN)}
                        bType={buttonType}
                        extStyles={extStyles?.button}
                        loading={isRedirecting}
                    >
                        {buttonText}
                    </ST.SButton>
                </ST.Desc>
            </ST.DescCol>
            <ST.ImageCol extStyles={extStyles?.imageCol} lg={6} md={6} sm={6} xs={6}>
                {advertising && (
                    <NonStaticContainer
                        $desktop={{
                            $bottom: 24,
                            $right: 24,
                        }}
                        $mobile={{
                            $bottom: 12,
                            $right: 12,
                        }}
                    >
                        <AdvertisingLabelWithTooltip
                            onClick={e => e.stopPropagation()}
                            companyName={advertising.companyName}
                            erid={advertising.erid}
                            tooltip={{
                                position: tooltipPosition,
                            }}
                        />
                    </NonStaticContainer>
                )}
                <WebpImage urlWebp={imageWebp || undefined}>
                    <ST.Image $extStyles={extStyles?.image} src={image} alt='изображение баннера'/>
                </WebpImage>
                {legal?.text && (
                    <ST.Legal textColor={legal.textColor}>
                        { legal.ageLimit && <ST.LegalTitle>{legal.ageLimit}+</ST.LegalTitle> }
                        <ST.LegalDescription>{legal.text}</ST.LegalDescription>
                    </ST.Legal>
                )}
            </ST.ImageCol>
        </ST.Wrapper>
    );
});

PromoBanner.displayName = 'PromoBannerWithForwardedRef';
export default PromoBanner;
